import {
  DEFAULT_INPUTS,
  DEFAULT_RAMP_PCTS,
  INDUSTRY_DEFAULTS,
  IndustryType,
  Inputs,
  getLeaderMonths,
} from './roiCalculations'

// Summer program preset (months counted from signing): ads paused Months
// 6–12, sales paused Months 1–3 and 10–12.
export const SUMMER_SPEND_PAUSE_MONTHS = [6, 7, 8, 9, 10, 11, 12]
export const SUMMER_SALES_PAUSE_MONTHS = [1, 2, 3, 10, 11, 12]
export const SUMMER_PEST: IndustryType = 'Summer Pest'
// Summer Pest reps hired before the season start selling in Month 4.
export const SUMMER_PEST_LAUNCH_MONTH = 4
// Summer Pest preset: a new rep's sales in months 1–3 after launch.
export const SUMMER_PEST_RAMP_SALES: [number, number, number] = [20, 35, 45]

// Stored as ramp percentages of the veteran rate.
const summerPestRamp = (vet: number) => ({
  rampMonth1Pct: (SUMMER_PEST_RAMP_SALES[0] / vet) * 100,
  rampMonth2Pct: (SUMMER_PEST_RAMP_SALES[1] / vet) * 100,
  rampMonth3Pct: (SUMMER_PEST_RAMP_SALES[2] / vet) * 100,
})

/**
 * Turns the summer program preset on or off. Off undoes only the preset's own
 * pauses: spend comes back only in months still at $0, and only the preset's
 * sales pauses are removed, so any edits made since are kept.
 */
export function withSummerProgram(inputs: Inputs, on: boolean): Inputs {
  if (!!inputs.summerProgram === on) return inputs
  const leaderMonths = getLeaderMonths(inputs)
  const indexForMonth = (month: number) => month - 1 - leaderMonths
  const spend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))]
  const campaigns = [
    ...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns)),
  ]
  const salesPauses = new Set(inputs.pausedSalesMonths || [])

  if (on) {
    const restore: Record<number, [number, number]> = {}
    SUMMER_SPEND_PAUSE_MONTHS.forEach((month) => {
      const idx = indexForMonth(month)
      if (idx < 0 || idx > 11) return
      restore[idx] = [spend[idx], campaigns[idx]]
      spend[idx] = 0
      campaigns[idx] = 0
    })
    SUMMER_SALES_PAUSE_MONTHS.forEach((month) => salesPauses.add(month - 1))
    return {
      ...inputs,
      summerProgram: true,
      summerRestore: restore,
      monthlyAdSpend: spend,
      monthlyCampaigns: campaigns,
      pausedSalesMonths: [...salesPauses].sort((a, b) => a - b),
    }
  }

  Object.entries(inputs.summerRestore || {}).forEach(([key, [s, c]]) => {
    const idx = Number(key)
    if (spend[idx] * campaigns[idx] === 0) {
      spend[idx] = s
      campaigns[idx] = c
    }
  })
  SUMMER_SALES_PAUSE_MONTHS.forEach((month) => salesPauses.delete(month - 1))
  return {
    ...inputs,
    summerProgram: false,
    summerRestore: undefined,
    monthlyAdSpend: spend,
    monthlyCampaigns: campaigns,
    pausedSalesMonths: [...salesPauses].sort((a, b) => a - b),
  }
}

/**
 * Switches industry and applies its defaults. Summer Pest also turns on the
 * summer program and a Month 4 launch; leaving it turns both back off.
 */
export function withIndustry(inputs: Inputs, industry: IndustryType): Inputs {
  const defaults = INDUSTRY_DEFAULTS[industry]
  const next: Inputs = {
    ...inputs,
    industry,
    ticket: defaults.ticket,
    gpm: defaults.gpm,
    salesFirst90Total: defaults.salesFirst90Total,
    salesVetMonthly: defaults.salesVetMonthly,
    revenueCadence: defaults.revenueCadence,
    residualTicket: defaults.residualTicket,
    // Pest industries default to 90% pull-through. Others keep the current
    // value, unless coming from a pest industry, which resets it to 80%.
    pullthrough:
      defaults.pullthrough ??
      (INDUSTRY_DEFAULTS[inputs.industry]?.pullthrough !== undefined
        ? DEFAULT_INPUTS.pullthrough
        : inputs.pullthrough),
  }
  const wasSummer = inputs.industry === SUMMER_PEST
  if (industry === SUMMER_PEST && !wasSummer) {
    return withSummerProgram(
      {
        ...next,
        launchMonth: SUMMER_PEST_LAUNCH_MONTH,
        ...summerPestRamp(next.salesVetMonthly),
      },
      true,
    )
  }
  if (wasSummer && industry !== SUMMER_PEST) {
    return withSummerProgram(
      {
        ...next,
        launchMonth: 0,
        rampMonth1Pct: DEFAULT_RAMP_PCTS[0],
        rampMonth2Pct: DEFAULT_RAMP_PCTS[1],
        rampMonth3Pct: DEFAULT_RAMP_PCTS[2],
      },
      false,
    )
  }
  return next
}
